import type { TimelineItemOut } from "@/api";

/**
 * How the last request for a Vorgang ended, as far as the record's timeline
 * says it (round-3 screen check, R3-12; UX gate round 3, fix #4): „Neu
 * übergeben …" after a refusal opened an empty dialog — who had declined,
 * and why, stood only in the timeline behind the modal.
 *
 * Read from the timeline because the record carries nothing else: its
 * `handover` is the open request only. The decline's actor is the person
 * who declined (`workflow.handover.decline_handover`); the reason is the
 * quote the server's sentence closes with (`register_summary._declined`),
 * the person an expired request went to is named in its sentence
 * (`_expired`). A sentence that does not have that shape says nothing here
 * — no context rather than a wrong one.
 */
export interface PreviousRequest {
  outcome: "declined" | "expired";
  /** Who declined, or whom the lapsed request went to. */
  name: string;
  /** What the person who declined wrote — `null` without a reason, and for a lapse. */
  reason: string | null;
  at: string;
}

const HANDOVER_EVENT = "case_handover_";
const DECLINED = /^(.+?) hat die Übergabe abgelehnt(?:: „([\s\S]*)“\.?|\.)$/;
const EXPIRED = /^Übergabe an (.+) ist verfallen\.$/;

export function previousRequest(timeline: readonly TimelineItemOut[]): PreviousRequest | null {
  let last: { eventType: string; at: string; id: number; summary: string; actor: string | null } | null = null;
  for (const item of timeline) {
    if (item.kind !== "event" || !item.event.eventType.startsWith(HANDOVER_EVENT)) continue;
    const { eventType, at, id, summary, actorDisplayName } = item.event;
    const later = last === null || at > last.at || (at === last.at && id > last.id);
    if (later) last = { eventType, at, id, summary, actor: actorDisplayName };
  }
  if (last === null) return null;
  if (last.eventType === "case_handover_declined") {
    const said = DECLINED.exec(last.summary);
    if (said === null) return null;
    const reason = said[2]?.trim() ?? "";
    return { outcome: "declined", name: last.actor ?? said[1]!, reason: reason === "" ? null : reason, at: last.at };
  }
  if (last.eventType === "case_handover_expired") {
    const said = EXPIRED.exec(last.summary);
    return said === null ? null : { outcome: "expired", name: said[1]!, reason: null, at: last.at };
  }
  return null;
}
